import { cn } from "@/lib/cn";

import { ProfitBadge } from "@/components/calendar";
import { CounterBadge } from "@/components/ui/CounterBadge";
import { DatePicker } from "@/components/ui/DatePicker";
import { PeriodPicker } from "@/components/ui/PeriodPicker";
import { Skeleton } from "@/components/ui/skeleton";
import { STATS_PERIOD_PRESETS } from "@/constants/periods";

// ============ Types ============
export interface ProfitStatRowData {
  date: string;
  profit: number;
  percent: number;
  trades: number;
}

// ============ Profit Stat Row ============
const ProfitStatRow = ({ date, profit, percent, trades }: ProfitStatRowData) => (
  <div className="flex items-center justify-between border-b border-neutral-700 py-4">
    {/* Date */}
    <span className="fs-300 w-26 mr-3 whitespace-nowrap font-semibold text-white">{date}</span>

    {/* Profit Info */}
    <div className="flex w-78.5 items-center justify-center gap-2 whitespace-nowrap">
      {/* Profit Change */}
      <div className="flex flex-1 justify-end">
        <ProfitBadge
          value={profit}
          type="currency"
          size="md"
          showArrow={false}
        />
      </div>

      {/* Percent */}
      <div className="flex flex-1 justify-start">
        <ProfitBadge
          value={percent}
          type="percent"
          size="md"
          showArrow={false}
        />
      </div>
    </div>

    {/* Trades Counter */}
    <div className="flex w-26 justify-end">
      <CounterBadge value={trades} />
    </div>
  </div>
);

// ============ Table Legend ============
const TableLegend = () => (
  <div
    className={cn(
      "flex items-center justify-between border-b border-neutral-200/4 py-2",
      "fs-200 font-medium text-neutral-400"
    )}
  >
    <span className="w-26 mr-3 whitespace-nowrap">Дата</span>

    {/* Центральная группа из 2 колонок */}
    <div className="flex w-78.5 items-center justify-center gap-2 whitespace-nowrap">
      <span>USDT</span>
      <span className="opacity-60">|</span>
      <span>%</span>
    </div>

    <span className="w-26 whitespace-nowrap text-right">Сделок</span>
  </div>
);

// ============ Date Input with DatePicker ============
interface DateInputProps {
  label: string;
  value?: Date;
  onChange: (value: Date | undefined) => void;
}

const DateInput = ({ label, value, onChange }: DateInputProps) => (
  <div className="flex min-w-40 flex-1 flex-col gap-1">
    <span className="fs-300 px-4 font-semibold text-neutral-200/50">{label}</span>
    <DatePicker
      value={value}
      onChange={onChange}
    />
  </div>
);

// ============ Stats List View ============
export interface StatsListViewProps {
  listData: ProfitStatRowData[];
  period: string;
  onPeriodChange: (value: string) => void;
  dateFrom?: Date;
  dateTo?: Date;
  onDateFromChange: (value: Date | undefined) => void;
  onDateToChange: (value: Date | undefined) => void;
  isLoading?: boolean;
  /** Слот для кнопок справа (например, переключатель view mode) */
  headerActions?: React.ReactNode;
}

export const StatsListView = ({
  listData,
  period,
  onPeriodChange,
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  isLoading,
  headerActions,
}: StatsListViewProps) => {
  return (
    <div className="flex w-full flex-col gap-2 md:w-134">
      {/* Filter Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 p-2">
          <span className="fs-300 font-semibold text-neutral-200/50">Показать за</span>
          <PeriodPicker
            options={STATS_PERIOD_PRESETS}
            value={period}
            onChange={onPeriodChange}
            size="md"
          />
        </div>
        {headerActions && <div className="flex items-center gap-2">{headerActions}</div>}
      </div>

      {/* Profit List */}
      <div className="scrollbar-hidden overflow-x-auto">
        <div className="flex min-w-[340px] flex-col">
          <TableLegend />

          {/* Scrollable Container with gradient overlay */}
          <div className="relative">
            {/* Gradient overlay top/bottom */}
            <div
              className="pointer-events-none absolute inset-0 z-10"
              style={{
                background:
                  "linear-gradient(180deg, var(--color-neutral-950) 0%, transparent 22%, transparent 80%, var(--color-neutral-950) 100%)",
              }}
            />

            {/* Content */}
            <div className="scrollbar-hidden max-h-63.5 overflow-y-auto">
              {isLoading ? (
                <div className="flex flex-col gap-2 py-4">
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton
                      key={i}
                      className="h-14 w-full"
                    />
                  ))}
                </div>
              ) : listData.length === 0 ? (
                <div className="py-8 text-center text-neutral-500">
                  Нет данных за выбранный период
                </div>
              ) : (
                listData.map((row) => (
                  <ProfitStatRow
                    key={row.date}
                    {...row}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Date Range Container */}
      <div className="flex flex-col gap-2 md:flex-row md:items-end">
        <DateInput
          label="От"
          value={dateFrom}
          onChange={onDateFromChange}
        />
        <DateInput
          label="До"
          value={dateTo}
          onChange={onDateToChange}
        />
      </div>
    </div>
  );
};
